//Router: */edit-profile
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackProfilePicture } from '../components/xpUtils';
import { deleteMyAccount } from '../api/auth';
import './EditProfile.css'; 
import backIcon from '../images/Back icon.svg';
import { suppressNextTokenChange } from '../utils/tokenWatcher';
import { clearSessionExpiredFlag } from '../utils/sessionBus';
const EditProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    day_of_birth: '',
    gender: '',
    photo: null, // For image upload
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [hadProfilePicture, setHadProfilePicture] = useState(false);
  const [dobInputType, setDobInputType] = useState('text');
  const [dobError, setDobError] = useState("");
  // Required fields part
  const requiredFields = ["first_name", "last_name"];
  
  // delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // Fetch current profile data
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch('https://dev-api.curu.com.au/api/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Failed to fetch profile: ${response.statusText}`);
        const data = await response.json();
        setProfile(data);
        
        // Check if user already had a profile picture
        const alreadyHadPicture = data.photo && data.photo.trim() !== '';
        setHadProfilePicture(alreadyHadPicture);
        
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          day_of_birth: data.day_of_birth || '',
          gender: data.gender || '',
          photo: null, // Initialize as null
        });
        setPreviewImage(data.photo || '/default-profile-pic.jpg');
      } catch (error) {
        setError('Failed to load profile. Please try again later.');
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo: file });

      // Preview the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  // Check the required fields
  const isFormValid = requiredFields.every(
  (k) => String(formData[k] ?? "").trim() !== ""
);
  const handleSubmit = async (e) => {
    e.preventDefault();

    setUploading(true);
    setError(null);

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const payload = new FormData();
      payload.append('first_name', formData.first_name);
      payload.append('last_name', formData.last_name);
      payload.append('day_of_birth', formData.day_of_birth);
      payload.append('gender', formData.gender);
      if (formData.photo) {
        payload.append('photo', formData.photo);
      }

      const response = await fetch('https://dev-api.curu.com.au/api/profile', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      const updatedProfile = await response.json();
      
      // Check if this is the first time adding a profile picture
      const nowHasProfilePicture = updatedProfile.photo && updatedProfile.photo.trim() !== '';
      const isFirstTimeAddingPicture = !hadProfilePicture && nowHasProfilePicture && formData.photo;
      
      if (isFirstTimeAddingPicture && profile?.id) {
        console.log("🎯 First time adding profile picture in EditProfile");
        
        // Check if XP was already awarded to this user
        const hasProfilePicXP = localStorage.getItem(`profilePicXPShown_${profile.id}`);
        
        if (!hasProfilePicXP) {
          console.log("✅ Awarding profile picture XP");
          trackProfilePicture();
          localStorage.setItem(`profilePicXPShown_${profile.id}`, 'true');
          
          // Dispatch event to trigger XP popup
          setTimeout(() => {
            console.log("🚀 Dispatching check-xp-earned event for profile picture");
            window.dispatchEvent(new CustomEvent('check-xp-earned'));
          }, 1000);
        } else {
          console.log("⚠️ Profile picture XP already awarded for this user");
        }
      }
      
      setProfile(updatedProfile);
      navigate('/user-profile', { state: { updated: true } });
    } catch (error) {
      setError(error.message || 'An error occurred while updating the profile.');
      console.error('Error updating profile:', error);
    } finally {
      setUploading(false);
    }
  };

  // Open/Close the delete dialog box
  const openDeleteModal = () => {
    setDeleteError("");
      setDeleteSuccess(false);
      setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
      if (!isDeleting) {
        setShowDeleteModal(false);
        setDeleteSuccess(false);
      }
  };

    // Ensure delete
  const handleConfirmDelete = async () => {
    setDeleteError("");
    setIsDeleting(true);
    try {
      await deleteMyAccount();
      // Success: Clear the local session and display the success status
      setDeleteSuccess(true);
      suppressNextTokenChange();
      clearSessionExpiredFlag();
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
    } catch (e) {
      console.error(e);
      setDeleteError(e?.message || "Failed to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };


  if (error) return <p className="error-message">{error}</p>;
  if (!profile) return null;

  return (

    <div className="edit-profile-page relative">
      <div className="edit-profile-container ">
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/user-profile')}
          aria-label="Back to profile"
        >
          <img src={backIcon} alt="" aria-hidden="true" />
        </button>
        <h2>Edit Profile</h2>
        <form onSubmit={handleSubmit} className="edit-profile-form">
          <div className="photo-upload">
            <div className="photo-preview">
              <img src={previewImage} alt="Profile Preview" className="preview-image" />
              <label className="edit-icon" htmlFor="photo"> 
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.4974 8.16629L11.8308 4.49963M1.28906 18.708L4.3914 18.3633C4.77043 18.3211 4.95995 18.3001 5.13709 18.2427C5.29425 18.1919 5.44381 18.12 5.58171 18.029C5.73715 17.9265 5.87198 17.7917 6.14165 17.522L18.2474 5.41629C19.2599 4.40377 19.2599 2.76215 18.2474 1.74963C17.2349 0.737105 15.5933 0.737103 14.5808 1.74962L2.47498 13.8554C2.20531 14.125 2.07048 14.2599 1.96798 14.4153C1.87704 14.5532 1.80516 14.7028 1.75428 14.8599C1.69694 15.0371 1.67588 15.2266 1.63377 15.6056L1.28906 18.708Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </label>
              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/*"
                onChange={handlePhotoChange}
                className="file-input"
              />
            </div>
            {/* <p className="add-profile-picture">Add profile picture</p> */}
          </div>

          <div className="edit-form">
            <div className="form-group">
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="First Name(required)"
                required
                className="input-large"
              />
            </div>

            <div className="form-group">
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Last Name (required)"
                required
                className="input-large"
              />
            </div>

            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                value={profile.email}
                placeholder="Email"
                disabled
                readOnly
                className="input-large readonly-field"
              />
            </div>

            <div className="form-group">
              <input
                type={dobInputType}
                id="day_of_birth"
                name="day_of_birth"
                value={formData.day_of_birth || ''}
                onChange={(e) => {
                  const { value } = e.target;
                  setFormData({ ...formData, day_of_birth: value });

                  if (value && value > "2025-07-01") {
                    setDobError("Date of birth must be on or before 2025-07-01.");
                  } else {
                    setDobError("");
                  }
                }}
                onFocus={() => setDobInputType("date")}
                onBlur={(e) => {
                  if (!e.target.value) setDobInputType("text");
                }}
                placeholder="Enter birthday (optional)"
                className={`input-large ${dobError ? "input-error" : ""}`}
                max="2025-07-01"
              />
              {dobError && <small className="field-error">{dobError}</small>}
            </div>

            <div className="form-group">
              <select
                id="gender"
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
                className={`input-large select-gender ${!formData.gender ? 'is-placeholder' : ''}`}
              >
                <option value="">
                  {'Gender (optional)'}
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          
          {/* Delete Account */}
          <div className="form-actions button-row" >
            <button
                type="button"
                className="delete-account-button delete-account-outline"
                onClick={openDeleteModal}
            >
                Delete Account
            </button>
        
            <button 
            type="submit" 
            disabled={uploading || !isFormValid || dobError} 
            className="primary-button"
            aria-disabled={uploading || !isFormValid || dobError}
            title={!isFormValid ? "Please complete required fields" : undefined}
            >
              {uploading ? 'Saving...' : 'Save'}
            </button>
          </div>

        </form>
        {showDeleteModal && (
          <div
            onClick={closeDeleteModal}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1050
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(520px, 92vw)",
                background: "#fff",
                borderRadius: 12,
                padding: 20,
                boxShadow: "0 10px 30px rgba(0,0,0,.2)"
              }}
            >
              {/* head */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h5 style={{ margin: 0, color: deleteSuccess ? "#198754" : "#dc3545" }}>
                  {deleteSuccess ? "Account deleted" : "Delete account"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  aria-label="Close"
                  style={{ border: "none", background: "transparent" }}
                />
              </div>

              {/* content */}
              {!deleteSuccess ? (
                <>
                  <p style={{ marginTop: 12, color: "#555" }}>
                    This will permanently delete your account and related personal data.
                  </p>

                  {deleteError && (
                    <div className="alert alert-danger" style={{ marginTop: 12 }}>
                      {deleteError}
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeDeleteModal}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleConfirmDelete}
                      disabled={isDeleting || !profile?.id}
                    >
                      {isDeleting ? "Deleting..." : "Confirm delete"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="alert alert-success" style={{ marginTop: 12 }}>
                    Your account has been deleted successfully.
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                    <a
                      href="/"
                      className="btn btn-success"
                      onClick={() => {
                          suppressNextTokenChange();
                          clearSessionExpiredFlag();
                          localStorage.removeItem("token");
                          localStorage.removeItem("refresh_token");
                      }}
                    >
                      Back to Login / Home
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EditProfile;